import type { CountUpAnimationArgs, CountUpProps } from "@/types/ui";
import { useEffect, useRef, useState } from "react";

const prefersReducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const runCountFrame = (now: number, args: CountUpAnimationArgs) => {
  const elapsed = now - args.start;
  const progress = Math.min(elapsed / args.duration, 1);
  const current = args.from + (args.to - args.from) * easeOutCubic(progress);
  args.setDisplay(current);
  if (progress < 1) args.frameRef.current = requestAnimationFrame((n) => runCountFrame(n, args));
  else args.fromRef.current = args.to;
};

const CountUp = ({ value, format = (n) => String(Math.round(n)), duration = 900, className }: CountUpProps) => {
  const [display, setDisplay] = useState(prefersReducedMotion() ? value : 0);
  const frameRef = useRef<number | null>(null);
  const fromRef = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay(value);
      return;
    }

    const args: CountUpAnimationArgs = {
      start: performance.now(),
      from: fromRef.current,
      to: value,
      duration,
      setDisplay,
      frameRef,
      fromRef,
    };

    frameRef.current = requestAnimationFrame((n) => runCountFrame(n, args));

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      fromRef.current = value;
    };
  }, [value, duration]);

  return <span className={className}>{format(display)}</span>;
};

export { CountUp };
